import {
  BYTE_UNIT,
  BYTE_UNITS,
  DEFAULT_INVALID,
  DEFAULT_REPLACE,
  NUMBER_EXPRESSION,
} from 'cyprus/constants';
import { FORMAT_TYPE } from 'cyprus/enums';
import { FormatValue, FormatValueOptions } from 'cyprus/types';
import { abbreviateNumber, invalidNumber } from 'cyprus/utils';

export const formatNumber = (value: FormatValue, options: FormatValueOptions): string => {
  const { format } = options;
  const ignore = options.ignore || [];
  const decimal = Number(options.decimal || 0);
  const scale = Number(options.scale || 0);
  const number = Number(value);
  const match = ignore.includes(String(value));

  if (match) {
    return options.replace || DEFAULT_REPLACE;
  }

  if (invalidNumber(value)) {
    return options.invalid || DEFAULT_INVALID;
  }

  const negative = Math.sign(number) === -1;
  const abs = Math.abs(number);

  switch (format) {
    case FORMAT_TYPE.NUMBER: {
      // determine nearest decimal with max decimal passed in
      // ex. decimals = 3, 12345.02321 => 12,345.023
      // ex. decimals = 3, 12345.2 => 12,345.2
      // ex. decimals = 3, 12345.000003 => 12,345
      const rounded = Math.round(abs * Math.pow(10, decimal)) / Math.pow(10, decimal);

      if (negative) {
        return `-${String(rounded)
          .replace(NUMBER_EXPRESSION, ',')
          .replace(/(\.\d*),/g, '$1')}`;
      }
      return `${String(rounded)
        .replace(NUMBER_EXPRESSION, ',')
        .replace(/(\.\d*),/g, '$1')}`;
    }
    case FORMAT_TYPE.NUMBER_ABBREVIATED: {
      if (abs < 1000) {
        if (negative) {
          return `-${String(abs.toFixed(0))
            .replace(NUMBER_EXPRESSION, ',')
            .replace(/(\.\d*),/g, '$1')}`;
        }
        return `${String(abs.toFixed(0))
          .replace(NUMBER_EXPRESSION, ',')
          .replace(/(\.\d*),/g, '$1')}`;
      }
      if (negative) {
        return `-${abbreviateNumber(abs, decimal)}`;
      }
      return `${abbreviateNumber(abs, decimal)}`;
    }
    case FORMAT_TYPE.NUMBER_FIXED: {
      // fixed decimal no commas
      // ex. decimals = 3, 12,345.02321 => 12,345.023
      // ex. decimals = 3, 12,345.2 => 12.200
      // ex. decimals = 3, 12,345.000003 => 000
      if (negative) {
        return `-${String(abs.toFixed(decimal))
          .replace(NUMBER_EXPRESSION, ',')
          .replace(/(\.\d*),/g, '$1')}`;
      }
      return `${String(abs.toFixed(decimal))
        .replace(NUMBER_EXPRESSION, ',')
        .replace(/(\.\d*),/g, '$1')}`;
    }
    case FORMAT_TYPE.NUMBER_SCALE: {
      if (scale < 10) {
        const rounded = Math.round(abs * Math.pow(10, 1)) / Math.pow(10, 1);
        if (negative) {
          return `-${String(rounded)
            .replace(NUMBER_EXPRESSION, ',')
            .replace(/(\.\d*),/g, '$1')}`;
        }
        return `${String(rounded)
          .replace(NUMBER_EXPRESSION, ',')
          .replace(/(\.\d*),/g, '$1')}`;
      }
      if (scale < 1000) {
        if (negative) {
          return `-${String(abs.toFixed(0))
            .replace(NUMBER_EXPRESSION, ',')
            .replace(/(\.\d*),/g, '$1')}`;
        }
        return `${String(abs.toFixed(0))
          .replace(NUMBER_EXPRESSION, ',')
          .replace(/(\.\d*),/g, '$1')}`;
      }
      if (negative) {
        return `-${abbreviateNumber(number, decimal)}`;
      }
      return `${abbreviateNumber(number, decimal)}`;
    }
    case FORMAT_TYPE.NUMBER_BYTES: {
      return `${abbreviateNumber(number, decimal, BYTE_UNITS, BYTE_UNIT)}`;
    }
    default:
      return String(value || '');
  }
};
